import React, { useState } from "react"
import { Program, updateProgramStatus, fetchProgramList, fetchProgramById, fetchBankList } from "../../features/program/ProgramSlice"
import { setPreSelectedData } from "../../features/outlet/TenantSlice"
import { useAppDispatch } from "../../store/hook"
import { Settings, Power, PowerOff, X } from "lucide-react"
import { useNavigate } from "react-router-dom"

/* --------------------------------- Interfaces ------------------------------ */

interface ProgramsListProps {
  programList: Program[]
  principalId: string
  showEditModal: (show: null | string) => void;
}

// Confirmation Dialog Component
const ConfirmationDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}> = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
    aria-pressed={checked}
  >
    <span className="sr-only">Toggle Program Status</span>
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
)

const Programs: React.FC<ProgramsListProps> = ({
  programList,
  principalId,
  showEditModal
}) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  /* ------------------------------ State Management ---------------------------- */

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    program: Program | null;
    action: 'activate' | 'deactivate' | null;
  }>({
    isOpen: false,
    program: null,
    action: null
  });

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleProgramSelect = async (
    principalId: string,
    programId: string,
  ) => {
    dispatch(setPreSelectedData({ principalId, programId }))
    navigate("/outlets")
  }

  const handleStatusToggle = (program: Program) => {
    const action = program.status === "active" ? "deactivate" : "activate";
    setConfirmationDialog({
      isOpen: true,
      program,
      action
    });
  }

  const confirmStatusChange = async () => {
    if (!confirmationDialog.program) return;

    try {
      const response = await dispatch(updateProgramStatus({
        status: confirmationDialog.program.status === "active" ? "inactive" : "active",
        programId: confirmationDialog.program.programId
      }))
      if (response.type === "program/updateProgramStatus/fulfilled") {
        dispatch(fetchProgramList({ principalId }))
      }
    } catch (error) {
      console.error("Failed to update program status:", error)
    }
  }

  const closeConfirmationDialog = () => {
    setConfirmationDialog({
      isOpen: false,
      program: null,
      action: null
    });
  }

  /* ------------------------------ UI Handlers ---------------------------- */

  if (!programList.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-lg font-medium">No Programs Found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-8 mt-8 mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programList.map(program => (
          <div
            key={program.programId}
            className="bg-white rounded-xl hover:shadow-lg flex flex-col border border-cardborder transition-shadow duration-200"
          >
            {/* Brand Image at Top */}
            <div className="h-[180px] w-full bg-gray-100 rounded-t-lg overflow-hidden flex items-center justify-center">
              {program.brandImage ? (
                <img
                  src={program.brandImage}
                  alt={program.programName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300 text-2xl">No Image</div>
              )}
            </div>
            {/* Card Content with Padding */}
            <div className="p-4 flex flex-col flex-1">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-800 text-base truncate max-w-[75%]" title={program.programName}>{program.programName || "Program Name"}</span>
                <button
                  onClick={async () => {
                    const response = await dispatch(fetchProgramById(Number(program.programId)))
                    if (response.type === "program/fetchProgramById/fulfilled") {
                      showEditModal(program.programId)
                    }
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200"
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
              {/* Description */}
              <div className="mb-4 min-h-[48px] flex items-center">
                <p className="text-gray-600 text-sm line-clamp-2 w-full">
                  {program.description || "No description provided."}
                </p>
              </div>
              {/* Actions & Status */}
              <div className="flex items-center justify-between gap-2 mt-auto">
                <button
                  onClick={() => {
                    if (principalId)
                      handleProgramSelect(principalId, program.programId)
                  }}
                  className="px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-200 min-w-[110px]"
                >
                  View Outlets
                </button>
                <button
                  onClick={() => handleStatusToggle(program)}
                  className={`flex p-2 items-center justify-center  rounded-full transition-colors duration-200 border-none focus:outline-none ${
                    program.status === "active"
                      ? "hover:bg-gray-50 text-green-700"
                      : "hover:bg-gray-50 hover:text-red-700"
                  }`}
                  title={program.status === "active" ? "Deactivate" : "Activate"}
                >
                  {program.status === "active" ? (
                    <Power className="w-5 h-5 text-green-600 " />
                  ) : (
                    <PowerOff className="w-5 h-5 text-red-500   " />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={closeConfirmationDialog}
        onConfirm={confirmStatusChange}
        title={confirmationDialog.action === 'activate' ? 'Activate Program' : 'Deactivate Program'}
        message={
          confirmationDialog.program
            ? `Are you sure you want to ${confirmationDialog.action} the program "${confirmationDialog.program.programName}"?`
            : ''
        }
        confirmText={confirmationDialog.action === 'activate' ? 'Activate' : 'Deactivate'}
        cancelText="Cancel"
      />
    </div>
  )
}

export default Programs
